/*
 * Build Your Box gift synchronization.
 *
 * Reads the tier already computed server-side by snippets/build-your-box-logic.liquid
 * (via the <build-your-box-progress data-tier> element rendered in the cart drawer/page)
 * and keeps the free gift lines + Priority Dispatch cart attribute in sync with it.
 *
 * Every mutation goes through the same section-rendering path the theme's own
 * cart-items-component uses (request sections, morph them back in, dispatch
 * CartUpdateEvent) - a bare /cart/*.js fetch with no re-render leaves the
 * visible table's line positions stale relative to the server's cart, which
 * misdirects the next click on an unrelated line's quantity/remove control
 * (both key off a positional index, not a stable id).
*/
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { debounce, requestIdleCallback } from '@theme/utilities';

const CONFIG_URL = '/apps/smart-shipping/byob/config';
const GIFT_PROPERTY = '_byob_gift_type';
const PRIORITY_DISPATCH_ATTRIBUTE = 'Priority Dispatch';

let config = null;
let syncing = false;
let pendingResync = false;

async function loadConfig() {
  if (config) return config;
  const response = await fetch(CONFIG_URL, { credentials: 'same-origin' });
  if (!response.ok) {
    // Don't cache a failed fetch - a later sync() should retry rather than
    // silently no-op for the rest of the page's lifetime.
    return {};
  }
  config = await response.json();
  return config;
}

function numericVariantId(gidOrId) {
  const match = String(gidOrId || '').match(/(\d+)$/);
  return match ? Number(match[1]) : null;
}

function getTier() {
  const el = document.querySelector('build-your-box-progress');
  return el ? Number(el.dataset.tier || 0) : 0;
}

function cartItemsSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function refreshCartSections(cart) {
  if (!cart.sections) return;

  for (const el of document.querySelectorAll('cart-items-component')) {
    const sectionId = el.dataset.sectionId;
    const html = sectionId && cart.sections[sectionId];
    if (!html) continue;

    morphSection(sectionId, html, { mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
    el.dispatchEvent(
      new CartUpdateEvent(cart, sectionId, {
        source: 'byob-gift-sync',
        itemCount: cart.item_count,
        sections: cart.sections,
      }),
    );
  }
}

async function postJson(url, body) {
  const sectionIds = cartItemsSectionIds();
  const fullBody = sectionIds.length
    ? { ...body, sections: sectionIds.join(','), sections_url: window.location.pathname }
    : body;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(fullBody),
  });
  const cart = await response.json();
  refreshCartSections(cart);
  return cart;
}

async function sync() {
  if (syncing) {
    pendingResync = true;
    return;
  }
  syncing = true;

  try {
    const [cfg, cart0] = await Promise.all([
      loadConfig(),
      fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json()),
    ]);
    const dustBagId = numericVariantId(cfg.dustBagVariantId);
    const slippersId = numericVariantId(cfg.slippersVariantId);

    let cart = cart0;
    if (cart.item_count === 0) return;

    const tier = getTier();
    const cumulativeGifts = cfg.cumulativeGifts !== false;

    const dustBagLine = dustBagId ? cart.items.find((i) => i.variant_id === dustBagId) : null;
    const slippersLine = slippersId ? cart.items.find((i) => i.variant_id === slippersId) : null;

    const wantDustBag = tier >= 2 && (cumulativeGifts || tier < 3);
    const wantSlippers = tier >= 3;

    const itemsToAdd = [];
    if (dustBagId && wantDustBag && !dustBagLine) {
      itemsToAdd.push({
        id: dustBagId,
        quantity: 1,
        properties: { [GIFT_PROPERTY]: cfg.dustBagGiftType || 'dust_bag' },
      });
    }
    if (slippersId && wantSlippers && !slippersLine) {
      itemsToAdd.push({
        id: slippersId,
        quantity: 1,
        properties: { [GIFT_PROPERTY]: cfg.slippersGiftType || 'slippers' },
      });
    }

    const lineUpdates = {};
    if (dustBagId && !wantDustBag && dustBagLine) {
      lineUpdates[dustBagLine.key] = 0;
    } else if (dustBagId && wantDustBag && dustBagLine && dustBagLine.quantity !== 1) {
      // Heals a gift line quantity that was inflated before the quantity
      // selector was hidden for gift lines (e.g. an already-open cart).
      lineUpdates[dustBagLine.key] = 1;
    }
    if (slippersId && !wantSlippers && slippersLine) {
      lineUpdates[slippersLine.key] = 0;
    } else if (slippersId && wantSlippers && slippersLine && slippersLine.quantity !== 1) {
      lineUpdates[slippersLine.key] = 1;
    }

    const shouldDispatch = tier >= 3 ? 'Yes' : '';
    const attributesNeedUpdate =
      (cart.attributes?.[PRIORITY_DISPATCH_ATTRIBUTE] || '') !== shouldDispatch;
    const newAttributes = attributesNeedUpdate
      ? { ...cart.attributes, [PRIORITY_DISPATCH_ATTRIBUTE]: shouldDispatch }
      : null;
    const hasLineUpdates = Object.keys(lineUpdates).length > 0;

    if (itemsToAdd.length === 0 && !hasLineUpdates && !newAttributes) {
      return;
    }

    if (itemsToAdd.length > 0 && !hasLineUpdates) {
      // Crossing a tier threshold is the common case: adding a gift and
      // flipping Priority Dispatch at the same time. /cart/add.js accepts
      // attributes directly, so this is one slow round trip instead of two -
      // cart mutations on this store run ~1.5s+ each (other app webhooks on
      // the cart, not this script), so cutting one matters for how long the
      // gift takes to visibly show up.
      const body = { items: itemsToAdd };
      if (newAttributes) body.attributes = newAttributes;
      await postJson('/cart/add.js', body);
      return;
    }

    if (itemsToAdd.length > 0) {
      cart = await postJson('/cart/add.js', { items: itemsToAdd });
    }

    if (hasLineUpdates || newAttributes) {
      const body = {};
      if (hasLineUpdates) body.updates = lineUpdates;
      if (newAttributes) body.attributes = newAttributes;
      await postJson('/cart/update.js', body);
    }
  } finally {
    syncing = false;
    if (pendingResync) {
      pendingResync = false;
      sync();
    }
  }
}

/*
 * Trailing debounce so gift-sync's own cart mutation (adding/removing a
 * gift, flipping Priority Dispatch) never races a user's own in-flight
 * quantity edit on another line. Both key off a positional line index
 * baked into the last render (see the file header) - if gift-sync morphs
 * the section mid-edit, an already-queued debounced quantity request from
 * cart-items-component still carries the line index it captured before
 * the morph, and can land on the wrong line. Waiting for a quiet period
 * after the last cart:update means gift-sync only ever runs once the
 * user's own edits have fully landed, not while one is still in flight.
 */
const debouncedSync = debounce(sync, 450);

document.addEventListener('cart:update', debouncedSync);

/*
 * The initial reconciliation used to run on DOMContentLoaded. This module is
 * loaded on every page from layout/theme.liquid, so that put an app-proxy round
 * trip (/apps/smart-shipping/byob/config) plus /cart.js on the critical path of
 * every single pageview - including product pages that have no Build Your Box UI
 * anywhere on them. App proxies go out to the app server, so they are slow, and
 * the `cart.item_count === 0` early exit in sync() only happens *after* both
 * requests have been awaited.
 *
 * None of this work is urgent: it heals gift lines and the Priority Dispatch
 * attribute, which the customer only sees once they open the cart. So it now
 * waits for idle time instead of competing with the page's own scripts and with
 * the customer's first taps. Anything that actually changes the cart still
 * triggers the debounced sync through the cart:update listener above.
 *
 * The timeout matters: a genuinely busy page may never hand out an idle slot, and
 * this reconciliation does have to happen. 3s puts it well clear of load without
 * risking it never running at all.
 */
requestIdleCallback(() => sync(), { timeout: 3000 });
